import React, { FC, useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  Alert,
  CardMedia,
  Typography,
  Dialog,
  DialogContent,
  IconButton,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import clientService from 'src/clientService';
import { FileDownloadResponse, HttpResponse, HTTPValidationError } from 'src/api/Api';

interface InplaceImageProps {
  fileId: string;
  alt: string;
};

const InplaceImage: FC<InplaceImageProps> = ({ fileId, alt }) => {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hovered, setHovered] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const fetchImageUrl = async () => {
      if (!fileId) {
        if (isMounted) setLoading(false);
        return;
      }

      if (isMounted) {
        setLoading(true);
        setError(null);
      }

      try {
        const res: HttpResponse<FileDownloadResponse, HTTPValidationError> =
          await clientService.api.getSpectrogramUrlApiV1AdminFilesFileIdSpectrogramUrlGet(fileId);
        if (isMounted) {
          setImageUrl(res.data.download_url);
        }
      } catch (err) {
        console.error('Failed to get image URL:', err);
        if (isMounted) {
          setError('Failed to load image.');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchImageUrl();

    return () => {
      isMounted = false;
    };
  }, [fileId]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height={140}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height={140}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (imageUrl) {
    return (
      <>
        <Box
          position="relative"
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          sx={{ height: 140, cursor: 'pointer' }}
        >
          <CardMedia component="img" height="140" image={imageUrl} alt={alt} />
          {hovered && (
            <Box
              position="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              display="flex"
              justifyContent="center"
              alignItems="center"
              sx={{ backgroundColor: 'rgba(0, 0, 0, 0.4)' }}
            >
              <IconButton onClick={() => setDialogOpen(true)} sx={{ color: 'white' }}>
                <VisibilityIcon />
              </IconButton>
            </Box>
          )}
        </Box>
        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="lg">
          <DialogContent>
            <img src={imageUrl} alt={alt} style={{ width: '100%' }} />
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return (
    <Box display="flex" justifyContent="center" alignItems="center" height={140}>
      <Typography variant="body2" color="text.secondary">No spectrogram available.</Typography>
    </Box>
  );
};

export default InplaceImage;
